<template>
    <section>
        <CitizensFilters @on-filter-change="loadAsyncData" />

        <BmvTable
            :columns="tableColumns"
            :data="rows"
            :loading="loading"
            :pagination="true"
            :total-results="total"
            :total-pages="totalPages"
            :current-page="currentPage"
            :per-page="perPage"
            :empty-text="$t('company.citizens.empty')"
            @page-change="onPageChange"
        >
            <!-- ID -->
            <template #cell-id="{ row }">
                <span class="bmv-cell-id">#{{ row.id }}</span>
            </template>

            <!-- Name -->
            <template #cell-name="{ row }">
                <div class="tc-identity">
                    <span class="tc-avatar">{{ initials(row) }}</span>
                    <span class="tc-name" :class="{ 'tc-name--fallback': !hasRealName(row) }">
                        {{ displayName(row) }}
                    </span>
                </div>
            </template>

            <!-- Email -->
            <template #cell-email="{ row }">
                <a v-if="email(row)" :href="`mailto:${email(row)}`" class="tc-link">{{ email(row) }}</a>
                <span v-else class="tc-empty">—</span>
            </template>

            <!-- Phone -->
            <template #cell-phone="{ row }">
                <span v-if="phone(row)">{{ phone(row) }}</span>
                <span v-else class="tc-empty">—</span>
            </template>

            <!-- Local role -->
            <template #cell-role="{ row }">
                <BmvBadge :variant="roleVariant(row)" :icon="roleIcon(row)" size="sm">
                    {{ roleLabel(row) }}
                </BmvBadge>
            </template>

            <!-- Registration date -->
            <template #cell-created_at="{ row }">
                <span v-if="row.created_at" class="tc-date">{{ formatDate(row.created_at) }}</span>
                <span v-else class="tc-empty">—</span>
            </template>
        </BmvTable>
    </section>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState } from 'vuex'
import BmvTable from '@/components/UI/BmvTable'
import BmvBadge from '@/components/UI/v2/BmvBadge.vue'
import CitizensFilters from '@/components/Model/Company/CitizensFilters'
import { citizenEmail, citizenName, citizenPhone } from '@/helpers/citizenName'
import {
    CITIZEN_ROLE_ICONS,
    CITIZEN_ROLE_VARIANTS,
    normalizeCitizenRole
} from '@/config/citizenRoles'

export default {
    name: 'TabCitizens',
    components: {
        BmvTable,
        BmvBadge,
        CitizensFilters
    },
    props: {
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            tableColumns: [
                { field: 'id', label: 'ID', width: '70px' },
                { field: 'name', label: this.$t('companies.name') },
                { field: 'email', label: this.$t('companies.email') },
                { field: 'phone', label: this.$t('companies.phone'), width: '150px' },
                { field: 'role', label: this.$t('company.citizens.role'), width: '190px' },
                { field: 'created_at', label: this.$t('company.citizens.registered_at'), width: '130px', align: 'center' }
            ]
        }
    },
    computed: {
        ...mapState('company', {
            citizens: (state) => state.citizens,
            config: (state) => state.citizensConfig
        }),
        // The endpoint may answer with a paginated payload or a plain array.
        rows() {
            if (!this.citizens) return []
            return Array.isArray(this.citizens) ? this.citizens : (this.citizens.data || [])
        },
        total() {
            if (this.citizens && !Array.isArray(this.citizens) && this.citizens.total !== undefined) {
                return this.citizens.total
            }
            return this.rows.length
        },
        perPage() {
            if (this.citizens && !Array.isArray(this.citizens) && this.citizens.per_page) {
                return Number(this.citizens.per_page)
            }
            return this.config.per_page
        },
        currentPage() {
            if (this.citizens && !Array.isArray(this.citizens) && this.citizens.current_page) {
                return this.citizens.current_page
            }
            return this.config.page
        },
        totalPages() {
            if (!this.perPage) return 0
            return Math.ceil(this.total / this.perPage)
        }
    },
    methods: {
        displayName(row) {
            return citizenName(row) || citizenEmail(row) || this.$t('post.page.unknown')
        },
        // A name was found, so the email cell is not a duplicate of it.
        hasRealName(row) {
            return !!citizenName(row)
        },
        email(row) {
            return citizenEmail(row)
        },
        phone(row) {
            return citizenPhone(row)
        },
        initials(row) {
            const name = this.displayName(row)
            return name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part.charAt(0).toUpperCase())
                .join('')
        },
        roleVariant(row) {
            return CITIZEN_ROLE_VARIANTS[normalizeCitizenRole(row)] || 'neutral'
        },
        roleIcon(row) {
            return CITIZEN_ROLE_ICONS[normalizeCitizenRole(row)] || ''
        },
        roleLabel(row) {
            return this.$t('company.citizens.roles.' + normalizeCitizenRole(row))
        },
        formatDate(value) {
            const date = new Date(value)
            if (isNaN(date.getTime())) return value
            return new Intl.DateTimeFormat(this.$i18n.locale).format(date)
        },
        onPageChange(page) {
            this.config.page = page
            this.loadAsyncData()
        },
        loadAsyncData() {
            this.$emit('get-data')
        }
    }
}
</script>

<style scoped>
.tc-identity {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.tc-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 50%;
    background: rgba(8, 145, 178, 0.1);
    color: #0891B2;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.tc-name {
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* No name on the account: the email stands in for it. */
.tc-name--fallback {
    font-weight: 500;
    color: #64748b;
    font-style: italic;
}

.tc-link {
    color: #0891B2;
    text-decoration: none;
}

.tc-link:hover {
    text-decoration: underline;
}

.tc-date {
    color: #475569;
    font-size: 0.8rem;
}

.tc-empty {
    color: var(--v2-text-4, #94a3b8);
    font-size: 0.78rem;
}

@media (max-width: 640px) {
    .tc-avatar {
        display: none;
    }
}
</style>
